import { useEffect, useState } from 'react';

import {
  Agencies,
  getPharmacistReports,
  pharmacistReports,
  ReportRequestTypes,
  TypeOfUser,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import { DateTime } from 'luxon';
import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import useTableSort from '../useTableSort';

import {
  bookingReportHeader,
  bookingRequestRow,
} from '../../components/Invoice/BookingReport/helper';
import { MomentTimeFormats } from '../../helpers/Constants';
import useTableSelectCheckbox from '../useTableSelectCheckbox';

const today = DateTime.now();

const useBookingReport = (pharmacyId: string, isAutoSyncRecord?: boolean) => {
  const [page, setPage] = useState(1);

  const dispatch = useAppDispatch();

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const report = useAppSelector(pharmacistReports);

  const { data, totalCount } = report ?? {};
  const source = isAutoSyncRecord ? Agencies.PharmaPlan : Agencies.PharmaGap;

  const { isDesc, orderBy, handleSorting, order, resetSorting } = useTableSort();
  const { handlePress, selectedIds, selectAll, selectAllFunc } = useTableSelectCheckbox({
    page,
    userType: TypeOfUser.pharmacist,
    dataLength: data.length,
    useOfEdit: false,
  });

  const handlePagination = (_: unknown, selectedPage: number) => {
    const gotoPage = selectedPage;
    setPage(gotoPage);
    dispatch(
      getPharmacistReports({
        type: ReportRequestTypes.booked,
        page: gotoPage,
        sortBy: orderBy,
        desc: isDesc,
        pharmacyId,
        endDate: today.toFormat(MomentTimeFormats.fullDate),
        banners: [],
        source,
      }),
    );
  };

  const table = {
    headers: bookingReportHeader(),
    rows: bookingRequestRow(data, timeFormat, selectedIds, handlePress),
    headerBar: [],
  };

  const apiCall = () => {
    dispatch(
      getPharmacistReports({
        type: ReportRequestTypes.booked,
        page: 1,
        sortBy: orderBy,
        desc: isDesc,
        pharmacyId,
        endDate: today.toFormat(MomentTimeFormats.fullDate),
        banners: [],
        source,
      }),
    );
  };

  useEffect(() => {
    apiCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      apiCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return {
    order,
    orderBy,
    handleSorting,
    resetSorting,
    handlePagination,
    page,
    table,
    totalCount,
    data,
    selectAll,
    handleSelectAll: selectAllFunc,
    selectedIds,
  };
};

export default useBookingReport;
